'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { toast } from 'sonner';

import {
  validateSession as validateSessionAction,
  safeRefreshSession,
} from '@/lib/actions/auth.actions';

/* =============================================================================
 * TYPES
 * ============================================================================= */

export interface SessionContextValue {
  sessionValid: boolean;
  isLoading: boolean;
  lastChecked: number | null;
  error: string | null;

  refresh: (opts?: { silent?: boolean; force?: boolean }) => Promise<boolean>;
  invalidate: (reason?: string) => void;
  checkSession: (opts?: { force?: boolean }) => Promise<boolean>;
}

export interface SessionProviderProps {
  children: ReactNode;
  initialSessionValid?: boolean;
  skipInitialCheck?: boolean;
}

/* =============================================================================
 * CONFIG
 * ============================================================================= */

const SESSION_CONFIG = {
  REFRESH_INTERVAL_MS: 10 * 60 * 1000, // 10 minutes
  MAX_RETRIES: 2,
  RETRY_BASE_DELAY_MS: 2_500,
  INITIAL_CHECK_DELAY_MS: 1000, // Wait 1s before initial check
  MIN_TIME_BETWEEN_CHECKS: 60 * 1000, // 60 seconds
} as const;

const DEV = process.env.NODE_ENV === 'development';
const TAG = '[CLIENT][SessionProvider]';

/* =============================================================================
 * CONTEXT
 * ============================================================================= */

export const SessionContext = createContext<SessionContextValue | undefined>(undefined);

/* =============================================================================
 * INTERNAL HELPERS
 * ============================================================================= */

function sleep(ms: number): Promise<void> {
  return new Promise((res) => setTimeout(res, ms));
}

function backoffDelay(attempt: number): number {
  const exp = Math.pow(2, Math.max(0, attempt));
  const raw = SESSION_CONFIG.RETRY_BASE_DELAY_MS * exp;
  const capped = Math.min(raw, 20_000);
  const jitter = Math.floor(Math.random() * 250);
  return capped + jitter;
}

function safeJson(payload: unknown) {
  try {
    return payload ? JSON.parse(JSON.stringify(payload)) : undefined;
  } catch {
    return undefined;
  }
}

function dlog(event: string, payload?: Record<string, unknown>) {
  if (!DEV) return;
  console.log(`${TAG} ${event}`, payload ? safeJson(payload) : '');
}

function dwarn(event: string, payload?: Record<string, unknown>) {
  if (!DEV) return;
  console.warn(`${TAG} ⚠️ ${event}`, payload ? safeJson(payload) : '');
}

function derr(event: string, payload?: Record<string, unknown>) {
  console.error(`${TAG} ❌ ${event}`, payload ? safeJson(payload) : '');
}

function newOpId(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function classifySessionError(error: any): {
  isTransient: boolean;
  isAuthError: boolean;
  category: 'network' | 'server' | 'auth' | 'unknown';
} {
  const message = String(error?.message || '').toLowerCase();
  const code = String(error?.code || '').toLowerCase();
  const status = Number(error?.status || error?.statusCode);
  
  // Network/timeout errors
  const isNetworkError = 
    message.includes('network') || 
    message.includes('econn') ||
    message.includes('socket') ||
    message.includes('failed to fetch');
  
  const isTimeout = 
    message.includes('timeout') ||
    message.includes('timed out') ||
    message.includes('etimedout') ||
    message.includes('econnaborted');
  
  // Server errors (5xx)
  const isServerError = status >= 500 && status < 600;
  
  // Auth errors (401/403/419)
  const isAuthError = 
    status === 401 || status === 403 || status === 419 ||
    code.includes('unauthorized') || 
    code.includes('forbidden') ||
    code.includes('auth_expired') ||
    message.includes('session expired') ||
    message.includes('invalid token');
  
  const isTransient = isNetworkError || isTimeout || isServerError;
  
  let category: 'network' | 'server' | 'auth' | 'unknown' = 'unknown';
  if (isNetworkError || isTimeout) category = 'network';
  else if (isServerError) category = 'server';
  else if (isAuthError) category = 'auth';
  
  return {
    isTransient,
    isAuthError,
    category,
  };
}

/* =============================================================================
 * PROVIDER
 * ============================================================================= */

export function SessionProvider({
  children,
  initialSessionValid = true, // ✅ CRITICAL FIX: Start optimistic
  skipInitialCheck = false,
}: SessionProviderProps) {
  const pathname = usePathname();
  const router = useRouter();
  
  const mountedRef = useRef(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Deduplication and state tracking
  const inFlightCheckRef = useRef<Promise<boolean> | null>(null);
  const inFlightRefreshRef = useRef<Promise<boolean> | null>(null);
  const lastCheckTimeRef = useRef<number>(0);
  const retryCountRef = useRef(0);

  // ✅ CRITICAL FIX: Start optimistic - assume session is valid
  const [sessionValid, setSessionValid] = useState(initialSessionValid);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [lastChecked, setLastChecked] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const safeSetSessionValid = useCallback((next: boolean) => {
    if (!mountedRef.current) return;
    setSessionValid(next);
  }, []);

  const safeSetLoading = useCallback((next: boolean) => {
    if (!mountedRef.current) return;
    setIsLoading(next);
  }, []);

  const stopMonitoring = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      dlog('monitor:stop');
    }
  }, []);

  const invalidate = useCallback(
    (reason?: string) => {
      if (!mountedRef.current) return;

      retryCountRef.current = 0;
      safeSetSessionValid(false);
      stopMonitoring();
      setError(reason || 'Session invalidated');

      if (reason) dwarn('invalidate', { reason });
      else dlog('invalidate');
    },
    [safeSetSessionValid, stopMonitoring]
  );

  // Listen for global auth errors from apiClient
  useEffect(() => {
    const handleAuthError = (event: Event) => {
      const customEvent = event as CustomEvent<{ status: number }>;
      const status = customEvent.detail?.status;

      if (status === 401) {
        invalidate('401 Unauthorized from API');
        if (pathname !== '/login') {
          router.push('/login');
        }
      } else if (status === 403) {
        toast?.error('You do not have permission to access this page.');
        // If not already on a standard route, send them somewhere safe
        if (!pathname.startsWith('/dashboards/customer')) {
          router.push('/dashboards/customer'); // Default safe fallback
        }
      }
    };

    window.addEventListener('auth-error', handleAuthError);
    return () => window.removeEventListener('auth-error', handleAuthError);
  }, [invalidate, pathname, router]);

  /* ---------------------------------------------------------------------------
   * Session Check (optimistic, NEVER invalidates on transient errors)
   * ------------------------------------------------------------------------- */
  const checkSession = useCallback(async (opts?: { force?: boolean }): Promise<boolean> => {
    const force = opts?.force ?? false;
    const now = Date.now();
    
    // Prevent rapid session checks
    if (!force && now - lastCheckTimeRef.current < SESSION_CONFIG.MIN_TIME_BETWEEN_CHECKS) {
      dlog('checkSession:skipped-too-soon', {
        timeSinceLast: now - lastCheckTimeRef.current,
        minInterval: SESSION_CONFIG.MIN_TIME_BETWEEN_CHECKS,
      });
      return sessionValid; // Return current state
    }

    if (inFlightCheckRef.current) {
      dlog('checkSession:dedup-hit');
      return inFlightCheckRef.current;
    }

    const opId = newOpId('check');
    dlog('checkSession:start', { opId, force });

    const run = (async () => {
      try {
        const res = await validateSessionAction();
        
        const ok = res.status === 'AUTHENTICATED';
        
        // Only update state if we got a definitive answer
        safeSetSessionValid(ok);
        setLastChecked(now);
        lastCheckTimeRef.current = now;
        
        if (ok) {
          retryCountRef.current = 0;
          setError(null);
        } else {
          setError(res.error?.message || 'Session invalid');
        }

        dlog('checkSession:result', {
          opId,
          ok,
          retryCount: retryCountRef.current,
        });

        return ok;
      } catch (e: any) {
        const classification = classifySessionError(e);
        
        derr('checkSession:failed', {
          opId,
          message: String(e?.message ?? e),
          classification,
        });

        // ✅ CRITICAL FIX: Never invalidate on transient errors
        // Only invalidate on explicit auth errors
        if (classification.isAuthError) {
          safeSetSessionValid(false);
          setError('Authentication required');
        } else {
          // Transient error - preserve current session state
          setError('Temporary service issue');
          // Return current session state (don't change it)
        }

        return sessionValid; // Return current state on error
      } finally {
        inFlightCheckRef.current = null;
        dlog('checkSession:done', { opId });
      }
    })();

    inFlightCheckRef.current = run;
    return run;
  }, [sessionValid, safeSetSessionValid]);

  /* ---------------------------------------------------------------------------
   * Refresh (optimistic, preserves session on transient errors)
   * ------------------------------------------------------------------------- */
  const refresh = useCallback(async (opts?: { silent?: boolean; force?: boolean }): Promise<boolean> => {
    const silent = opts?.silent ?? true; // Default to silent
    const force = opts?.force ?? false;
    const now = Date.now();
    
    // Check if refresh is needed (unless forced)
    if (!force && sessionValid) {
      const timeSinceLastCheck = now - (lastChecked || 0);
      if (timeSinceLastCheck < 30000) { // 30 second cooldown
        dlog('refresh:skipped-too-soon', { timeSinceLastCheck });
        return true;
      }
    }

    if (inFlightRefreshRef.current) {
      dlog('refresh:dedup-hit');
      return inFlightRefreshRef.current;
    }

    const opId = newOpId('refresh');
    dlog('refresh:start', { opId, silent, force });

    const run = (async () => {
      try {
        // Use safe refresh that doesn't logout on transient errors
        const result = await safeRefreshSession();
        
        if (result.success) {
          retryCountRef.current = 0;
          safeSetSessionValid(true);
          setLastChecked(now);
          setError(null);
          
          dlog('refresh:success', { opId });
          return true;
        }
        
        // Handle failure
        if (result.shouldRetry) {
          // Transient error - preserve session
          dwarn('refresh:transient-error', {
            opId,
            error: result.error,
            shouldRetry: true,
          });
          
          retryCountRef.current += 1;
          setError(result.error || 'Temporary refresh issue');
          
          // Retry with backoff if not at max retries
          if (retryCountRef.current < SESSION_CONFIG.MAX_RETRIES) {
            const delay = backoffDelay(retryCountRef.current);
            dlog('refresh:retrying', { 
              opId, 
              attempt: retryCountRef.current + 1,
              delayMs: delay,
            });
            
            await sleep(delay);
            return await refresh({ silent: true, force: true });
          }
          
          // Too many retries - but STILL don't invalidate for transient errors
          return sessionValid; // Keep current state
        } else {
          // Non-transient error (auth error) - invalidate
          dwarn('refresh:auth-error', {
            opId,
            error: result.error,
            shouldRetry: false,
          });
          
          invalidate(result.error || 'Refresh failed');
          return false;
        }
      } catch (e: any) {
        const classification = classifySessionError(e);
        
        derr('refresh:unexpected-error', {
          opId,
          message: String(e?.message ?? e),
          classification,
        });

        // ✅ CRITICAL FIX: Never invalidate on transient errors
        if (classification.isAuthError) {
          invalidate('Authentication error during refresh');
          return false;
        } else {
          // Transient error - preserve session
          setError('Temporary service issue during refresh');
          return sessionValid; // Return current state
        }
      } finally {
        inFlightRefreshRef.current = null;
        dlog('refresh:done', { opId });
      }
    })();

    inFlightRefreshRef.current = run;
    return run;
  }, [sessionValid, lastChecked, safeSetSessionValid, invalidate]);

  /* ---------------------------------------------------------------------------
   * Initial Check (delayed and optimistic)
   * ------------------------------------------------------------------------- */
  useEffect(() => {
    mountedRef.current = true;
    dlog('mount', { 
      initialSessionValid, 
      skipInitialCheck,
      pathname,
    });

    if (skipInitialCheck) {
      safeSetLoading(false);
      return () => {
        mountedRef.current = false;
        dlog('unmount');
      };
    }

    // Delayed initial check to avoid race with server components
    const initTimer = setTimeout(async () => {
      if (!mountedRef.current) return;
      
      try {
        // Only check if we haven't already validated
        if (initialSessionValid) {
          await checkSession({ force: true });
        }
      } finally {
        safeSetLoading(false);
        dlog('initial-check:done');
      }
    }, SESSION_CONFIG.INITIAL_CHECK_DELAY_MS);

    return () => {
      mountedRef.current = false;
      clearTimeout(initTimer);
      dlog('unmount');
    };
  }, [initialSessionValid, skipInitialCheck, checkSession, safeSetLoading, pathname]);

  /* ---------------------------------------------------------------------------
   * Monitoring (only when session is valid)
   * ------------------------------------------------------------------------- */
  const startMonitoring = useCallback(() => {
    if (intervalRef.current) return;
    if (!sessionValid) return;
    
    intervalRef.current = setInterval(() => {
      if (!mountedRef.current) return;
      if (!sessionValid) return;
      
      // Only refresh if we haven't checked recently
      const now = Date.now();
      const timeSinceLastCheck = now - (lastChecked || 0);
      
      if (timeSinceLastCheck > SESSION_CONFIG.REFRESH_INTERVAL_MS - 60000) { // 1 minute buffer
        void refresh({ silent: true });
      }
    }, SESSION_CONFIG.REFRESH_INTERVAL_MS);

    dlog('monitor:start', { 
      everyMs: SESSION_CONFIG.REFRESH_INTERVAL_MS,
    });
  }, [sessionValid, lastChecked, refresh]);

  // Start/stop monitoring based on session state
  useEffect(() => {
    if (!mountedRef.current) return;

    if (sessionValid) {
      startMonitoring();
    } else {
      stopMonitoring();
    }

    return () => stopMonitoring();
  }, [sessionValid, startMonitoring, stopMonitoring]);

  /* ---------------------------------------------------------------------------
   * Context Value
   * ------------------------------------------------------------------------- */
  const value = useMemo<SessionContextValue>(
    () => ({
      sessionValid,
      isLoading,
      lastChecked,
      error,
      refresh,
      invalidate,
      checkSession,
    }),
    [sessionValid, isLoading, lastChecked, error, refresh, invalidate, checkSession]
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

/* =============================================================================
 * HOOKS
 * ============================================================================= */

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within SessionProvider');
  return ctx;
}

/**
 * Optional consumer for contexts like UserProvider that should not crash
 * when SessionProvider is not mounted above.
 */
export function useOptionalSession(): SessionContextValue | undefined {
  return useContext(SessionContext);
}